import { apiClient } from "../../../services/apiService";

/**
 * Client for the public performance API (/api/v1/performance/**) and
 * the admin agent analytics (/api/v1/admin/dashboard/performance/**).
 * The backend serializes these payloads with camelCase map keys.
 */

const PUBLIC_BASE = "/api/v1/performance";
const ADMIN_BASE = "/api/v1/admin/dashboard/performance";

// ── Types ────────────────────────────────────────────────────────────

/**
 * Which agent's track record to read. "ev" is the default and is never
 * serialized, so EV request URLs stay byte-identical to the single-track era
 * (same shared GET cache keys, same backend behavior).
 */
export type PerformanceTrack = "ev" | "sharp" | "traders" | "bpi";

export interface PerformanceAggregate {
  bets: number;
  settledBets: number;
  pendingBets: number;
  wonBets: number;
  lostBets: number;
  pushBets: number;
  voidBets: number;
  totalStaked: number;
  /** Stake across all bets incl. pending — money placed, not just settled. */
  totalPlacedStaked: number;
  totalExpectedProfit: number;
  totalActualProfit: number;
  expectedRoiPercent: number | null;
  actualRoiPercent: number | null;
  winRate: number | null;
  averageEvPercent: number | null;
  averageOdds: number | null;
}

export interface PerformanceSummary extends PerformanceAggregate {
  /** Configured bankroll — bet sizing only. Returns are computed off stakes. */
  startingBankroll: number;
  currentBankroll: number;
  bestDay: { date: string; actualProfit: number } | null;
  worstDay: { date: string; actualProfit: number } | null;
  currentMonthExpectedProfit: number;
  currentMonthActualProfit: number;
  /** Aggregate stake on today's slate (US Eastern game day). No per-bet detail. */
  todayStaked: number;
  todayBets: number;
  // ── Traders track only (mark-to-market): null/absent on EV and Sharp. ──
  realizedPnl?: number | null;
  unrealizedPnl?: number | null;
  openPositions?: number | null;
  openCostBasis?: number | null;
  openMarketValue?: number | null;
  averageEntryPrice?: number | null;
}

export interface AnalyticsRow extends PerformanceAggregate {
  group: string;
}

export interface AnalyticsResponse {
  groupBy: string;
  rows: AnalyticsRow[];
}

export type SettlementState =
  | "UPCOMING"
  | "LIVE"
  | "AWAITING_RESULT"
  | "RETRYING"
  | "NEEDS_YOUR_GRADE"
  | "SETTLED";

export interface TrackedBetDto {
  id: number;
  placedAt: string | null;
  eventStartTime: string | null;
  sport: string | null;
  league: string | null;
  event: string | null;
  market: string | null;
  marketType: string | null;
  selection: string | null;
  betType: string | null;
  side: string | null;
  line: number | null;
  oddsAmerican: number;
  marketWidth: number | null;
  sportsbook: string | null;
  evPercent: number;
  stakeAmount: number;
  expectedProfit: number;
  expectedRoiPercent: number;
  status: string;
  isSettled: boolean;
  settledAt: string | null;
  actualResultValue: number | null;
  actualProfit: number | null;
  actualRoiPercent: number | null;
  totalPayout: number | null;
  closingOddsAmerican: number | null;
  clvPercent: number | null;
  oddsRangeBucket: string | null;
  evRangeBucket: string | null;
  isLiveAtPlacement: boolean;
  agentRunId: number | null;
  /**
   * Where an open bet stands in auto-settlement, derived server-side:
   * UPCOMING, LIVE, AWAITING_RESULT, RETRYING, NEEDS_YOUR_GRADE, SETTLED.
   */
  settlementState?: SettlementState | null;
  /** One plain-English line explaining the state (null when obvious). */
  settlementNote?: string | null;
  settlementSource?: string | null;
  /** Parlay leg count (null for single bets). */
  legCount?: number | null;
  /** Admin-only extras. */
  origin?: string | null;
  userId?: number | null;
  nextAttemptAt?: string | null;
  gradingAttempts?: number;
  gradingError?: string | null;
  lastUnresolvedReason?: string | null;
  settlementDiagnosticJson?: string | null;
  lastClvFailureReason?: string | null;
}

export interface SettlementAssistResponse {
  betId: number;
  betMetadata: Record<string, unknown>;
  storedDiagnostics: Record<string, unknown> | null;
  providerEvidence: Array<Record<string, unknown>>;
  suggestedStatus: string | null;
  suggestedActualResultValue: number | null;
  confidence: "HIGH" | "MEDIUM" | "LOW" | string;
  explanation: string | null;
  sources: string[];
  warnings: string[];
  needsHumanReview: boolean;
  gptAvailable: boolean;
  gptUsed: boolean;
}

export interface BetsResponse {
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  bets: TrackedBetDto[];
  /**
   * Set by the backend when the caller lacks Bronze access: the ledger is
   * restricted to settled WON/LOST rows and live pending picks are withheld.
   */
  pendingHidden?: boolean;
  hiddenPendingBets?: number;
}

export interface CalendarDay extends PerformanceAggregate {
  date: string;
  // ── Traders track only: day equity movement breakdown. ──
  /** Realized P/L from markets that resolved this day. */
  realizedPnlToday?: number | null;
  /** Change in unrealized (open-position) value this day. */
  unrealizedDelta?: number | null;
  openPositions?: number | null;
  /** Running total P/L through this day. */
  totalPnlCum?: number | null;
}

// ── Traders track (Polymarket mark-to-market positions) ─────────────

export type TraderPositionStatus =
  | "OPEN"
  | "RESOLVED_WON"
  | "RESOLVED_LOST"
  | "RESOLVED_OTHER"
  | "VOID";

export interface TraderPositionDto {
  id: number;
  track: "TRADERS";
  openedAt: string | null;
  marketName: string | null;
  betName: string | null;
  side: string | null;
  category: string | null;
  eventSlug: string | null;
  marketSlug: string | null;
  endDate: string | null;
  whaleWallet: string | null;
  whaleEntryPrice: number | null;
  scoreAtEntry: number | null;
  boardTier: number | null;
  /** Prices are dollars in [0,1] — 0.42 = 42 cents. */
  entryPrice: number | null;
  stakeUsdc: number | null;
  shares: number | null;
  currentPrice: number | null;
  currentValue: number | null;
  markedAt: string | null;
  status: TraderPositionStatus | string;
  pnl: number | null;
  pnlPercent: number | null;
  resolvedAt: string | null;
  terminalPrice: number | null;
  realizedPnl: number | null;
}

export interface TradersPositionsResponse {
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  positions: TraderPositionDto[];
  /**
   * Set by the backend when the caller lacks Traders (Gold) access: the ledger
   * is restricted to resolved rows and live open positions are withheld.
   */
  openHidden?: boolean;
  hiddenOpenPositions?: number;
}

export interface TradersEquityPoint {
  date: string;
  totalPnl: number;
  realizedPnlCum: number;
  unrealizedPnl: number;
  costBasisOpen: number;
  marketValueOpen: number;
  costBasisTotal: number;
  openPositions: number;
}

export interface TradersEquityResponse {
  points: TradersEquityPoint[];
}

export interface CalendarResponse {
  month: string;
  days: CalendarDay[];
}

export interface PerformanceFilters {
  /**
   * Agent track to read. Path-based (never written to the query string of the
   * page URL) and omitted from request params when "ev" so EV URLs stay
   * cache-identical to before multi-track.
   */
  track?: PerformanceTrack;
  /** My Bets only: just the bets tracked as BPI bets. */
  bpi?: boolean;
  startDate?: string;
  endDate?: string;
  sport?: string;
  league?: string;
  sportsbook?: string;
  market?: string;
  status?: string;
  side?: string;
  search?: string;
  minOdds?: number;
  maxOdds?: number;
  minEv?: number;
  maxEv?: number;
  minStake?: number;
  maxStake?: number;
  minExpectedProfit?: number;
  maxExpectedProfit?: number;
  minActualProfit?: number;
  maxActualProfit?: number;
  minRoi?: number;
  maxRoi?: number;
  minLine?: number;
  maxLine?: number;
  oddsBucket?: string;
  evBucket?: string;
  settled?: boolean;
  isLive?: boolean;
  agentRunId?: number;
  /**
   * When true, the categorical filter groups combine with OR (a bet matches
   * if it's in ANY selected segment) instead of AND. Set by the "winning
   * segments" auto-default so it shows the union of every profitable slice.
   */
  orMode?: boolean;
}

export type InsightTone = "positive" | "caution" | "neutral";

export interface PerformanceInsight {
  title: string;
  detail: string;
  tone: InsightTone;
}

export interface PerformanceInsights {
  available: boolean;
  generatedAt?: string;
  dataThrough?: string;
  headline?: string;
  summary?: string;
  howToUse?: string;
  insights?: PerformanceInsight[];
}

export interface AgentRunDto {
  id: number;
  startedAt: string | null;
  finishedAt: string | null;
  status: string;
  opportunitiesSeen: number;
  betsPlaced: number;
  betsSkipped: number;
  bankrollAtRun: number | null;
  error: string | null;
}

export interface SkippedOpportunityDto {
  id: number;
  agentRunId: number;
  skippedAt: string | null;
  skipReason: string;
  detail: string | null;
  league: string | null;
  event: string | null;
  marketType: string | null;
  selection: string | null;
  betType: string | null;
  sportsbook: string | null;
  oddsAmerican: number | null;
  evPercent: number | null;
}

export interface ConcentrationRow {
  group: string;
  bets: number;
  totalStaked: number;
  stakeSharePercent: number;
  overConcentrated: boolean;
}

export interface EvAgentConfig {
  enabled: boolean;
  startingBankroll: number;
  currentBankroll: number;
  kellyMultiplier: number;
  minEvPercent: number;
  maxEvPercent: number;
  minOddsAmerican: number;
  maxOddsAmerican: number;
  minMinutesToStart: number;
  maxBetsPerEvent: number;
  maxBetsPerDay: number;
  maxBetsPerRun: number;
  selectionIntervalMs: number;
  selectionIntervalMinutes: number;
  minStake: number;
  maxStake: number;
  includePredictionMarkets: boolean;
  settlementGraceHours: number;
  maxGradingAttempts: number;
  manualReviewAfterDays: number;
  skipRetentionDays: number;
  betsPlacedToday: number;
  selectionRunsPerDay: number;
  projectedMaxBetsPerDay: number;
  hasPersistedOverrides: boolean;
}

export type EvAgentConfigUpdate = Partial<
  Pick<
    EvAgentConfig,
    | "enabled"
    | "startingBankroll"
    | "kellyMultiplier"
    | "minEvPercent"
    | "maxEvPercent"
    | "minOddsAmerican"
    | "maxOddsAmerican"
    | "minMinutesToStart"
    | "maxBetsPerEvent"
    | "maxBetsPerDay"
    | "maxBetsPerRun"
    | "selectionIntervalMs"
    | "minStake"
    | "maxStake"
    | "includePredictionMarkets"
    | "settlementGraceHours"
    | "maxGradingAttempts"
    | "manualReviewAfterDays"
    | "skipRetentionDays"
  >
>;

export interface ClvReport {
  betsWithClv: number;
  betsWithoutClv: number;
  beatClosingLine: number;
  clvBeatRatePercent: number | null;
  averageClvPercent: number | null;
}

// ── Helpers ──────────────────────────────────────────────────────────

export const countActiveFilters = (filters: PerformanceFilters): number =>
  Object.entries(filters).filter(
    // orMode and track are modes, not filters — they never count as "filtered".
    ([k, v]) => k !== "orMode" && k !== "track" && v !== undefined && v !== null && v !== ""
  ).length;

export const hasActiveFilters = (filters: PerformanceFilters): boolean =>
  countActiveFilters(filters) > 0;

export const filtersToParams = (
  filters: PerformanceFilters,
  extra: Record<string, string | number | undefined> = {}
): string => {
  const params = new URLSearchParams();
  Object.entries({ ...filters, ...extra }).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    // EV is the default track — omit it so EV request URLs (and the shared GET
    // cache keys) stay identical to the single-track era.
    if (key === "track" && value === "ev") return;
    params.set(key, String(value));
  });
  return params.toString();
};

// ── Filter ↔ URL query string (shareable / bookmarkable filtered views) ──

const FILTER_STRING_KEYS: (keyof PerformanceFilters)[] = [
  "startDate", "endDate", "sport", "league", "sportsbook", "market",
  "status", "side", "search", "oddsBucket", "evBucket",
];
const FILTER_NUMBER_KEYS: (keyof PerformanceFilters)[] = [
  "minOdds", "maxOdds", "minEv", "maxEv", "minStake", "maxStake",
  "minExpectedProfit", "maxExpectedProfit", "minActualProfit", "maxActualProfit",
  "minRoi", "maxRoi", "minLine", "maxLine", "agentRunId",
];
const FILTER_BOOLEAN_KEYS: (keyof PerformanceFilters)[] = ["settled", "isLive", "orMode", "bpi"];

/** Every query-string key the filters own (so URL sync can clear stale ones). */
export const FILTER_PARAM_KEYS: string[] = [
  ...FILTER_STRING_KEYS,
  ...FILTER_NUMBER_KEYS,
  ...FILTER_BOOLEAN_KEYS,
] as string[];

/** Hydrate a filter object from the URL — type-coerced per field. */
export const parseFiltersFromSearch = (params: URLSearchParams): PerformanceFilters => {
  const filters: Record<string, unknown> = {};
  FILTER_STRING_KEYS.forEach((key) => {
    const v = params.get(key);
    if (v) filters[key] = v;
  });
  FILTER_NUMBER_KEYS.forEach((key) => {
    const v = params.get(key);
    if (v != null && v !== "" && !Number.isNaN(Number(v))) filters[key] = Number(v);
  });
  FILTER_BOOLEAN_KEYS.forEach((key) => {
    const v = params.get(key);
    if (v === "true" || v === "false") filters[key] = v === "true";
  });
  return filters as PerformanceFilters;
};

/**
 * Short-lived GET cache + in-flight dedup. The page fans out several identical
 * analytics calls on load (filter dropdowns + profitability lens hit the same
 * league/book/market endpoints); this coalesces concurrent duplicates and
 * serves a brief cache so a filter toggle back-and-forth is instant. Only
 * successful responses are cached — a failure (null) is never cached, so retry
 * works. Returns null ONLY on error (a successful-but-empty response is still
 * an object), which lets callers distinguish "failed" from "no data".
 */
const GET_TTL_MS = 15_000;
const responseCache = new Map<string, { at: number; data: unknown }>();
const inFlight = new Map<string, Promise<unknown>>();

async function get<T>(url: string): Promise<T | null> {
  const cached = responseCache.get(url);
  if (cached && Date.now() - cached.at < GET_TTL_MS) {
    return cached.data as T;
  }
  const existing = inFlight.get(url);
  if (existing) {
    return existing as Promise<T | null>;
  }
  const request = (async () => {
    try {
      const response = await apiClient.get(url);
      responseCache.set(url, { at: Date.now(), data: response.data });
      return response.data as T;
    } catch (error) {
      console.error("[performance] request failed", url, error);
      return null;
    } finally {
      inFlight.delete(url);
    }
  })();
  inFlight.set(url, request);
  return request as Promise<T | null>;
}

// ── Public endpoints ─────────────────────────────────────────────────

export const fetchPerformanceSummary = (filters: PerformanceFilters = {}) =>
  get<PerformanceSummary>(`${PUBLIC_BASE}/summary?${filtersToParams(filters)}`);

export const fetchPerformanceAnalytics = (
  filters: PerformanceFilters,
  groupBy: string
) =>
  get<AnalyticsResponse>(
    `${PUBLIC_BASE}/analytics?${filtersToParams(filters, { groupBy })}`
  );

export const fetchPerformanceBets = (
  filters: PerformanceFilters,
  page: number,
  size: number,
  sortBy = "placedAt",
  sortDir = "desc"
) =>
  get<BetsResponse>(
    `${PUBLIC_BASE}/bets?${filtersToParams(filters, { page, size, sortBy, sortDir })}`
  );

export const fetchPerformanceCalendar = (month: string, filters: PerformanceFilters = {}) =>
  get<CalendarResponse>(`${PUBLIC_BASE}/calendar?${filtersToParams(filters, { month })}`);

/** Suggested "winning segments" filter — categories whose ROI clears minRoi%. */
export interface AutoFilterResponse {
  minRoiPercent: number;
  minBets: number;
  filter: PerformanceFilters;
}

export const fetchPerformanceAutoFilter = (minRoi = 2) =>
  get<AutoFilterResponse>(`${PUBLIC_BASE}/auto-filter?minRoi=${minRoi}`);

export interface DayBetsResponse {
  date: string;
  bets: TrackedBetDto[];
}

/** Settled bets for one game day (US Eastern). Settled-only — no pending leak. */
export const fetchPerformanceDayBets = (date: string, track?: PerformanceTrack) =>
  get<DayBetsResponse>(
    `${PUBLIC_BASE}/calendar/bets?date=${date}${
      track && track !== "ev" ? `&track=${track}` : ""
    }`
  );

// ── Traders track fetchers ───────────────────────────────────────────

/** Position ledger for the Traders track — resolved-only for non-Gold callers. */
export const fetchTradersPositions = (page: number, size = 25) =>
  get<TradersPositionsResponse>(
    `${PUBLIC_BASE}/bets?track=traders&page=${page}&size=${size}`
  );

/** Daily equity curve for the Traders track (mark-to-market). */
export const fetchTradersEquity = () =>
  get<TradersEquityResponse>(`${PUBLIC_BASE}/traders/equity`);

export interface TradersDayBetsResponse {
  date: string;
  bets: TraderPositionDto[];
}

/** Resolved positions for one day — resolved-only, open positions stay gated. */
export const fetchTradersDayBets = (date: string) =>
  get<TradersDayBetsResponse>(`${PUBLIC_BASE}/calendar/bets?date=${date}&track=traders`);

/** Daily AI read on the track record. Cached server-side; same for all visitors. */
export const fetchPerformanceInsights = () =>
  get<PerformanceInsights>(`${PUBLIC_BASE}/insights`);

// ── Admin endpoints ──────────────────────────────────────────────────

export const fetchAdminPerformanceSummary = (track: PerformanceTrack = "ev") =>
  get<PerformanceSummary>(
    `${ADMIN_BASE}/summary${track !== "ev" ? `?track=${track}` : ""}`
  );

export const fetchAdminPerformanceAnalytics = (
  filters: PerformanceFilters,
  groupBy: string
) =>
  get<AnalyticsResponse>(
    `${ADMIN_BASE}/analytics?${filtersToParams(filters, { groupBy })}`
  );

export const fetchAdminRuns = (page = 0, size = 25, track: PerformanceTrack = "ev") =>
  get<{ runs: AgentRunDto[]; totalPages: number; totalElements: number }>(
    `${ADMIN_BASE}/runs?page=${page}&size=${size}${track !== "ev" ? `&track=${track}` : ""}`
  );

export const fetchAdminSkips = (page = 0, size = 50, agentRunId?: number) =>
  get<{
    skips: SkippedOpportunityDto[];
    reasonBreakdown: { reason: string; count: number }[];
    totalPages: number;
    totalElements: number;
  }>(
    `${ADMIN_BASE}/skips?page=${page}&size=${size}${
      agentRunId ? `&agentRunId=${agentRunId}` : ""
    }`
  );

export const fetchAdminConcentration = () =>
  get<Record<string, ConcentrationRow[] | number>>(`${ADMIN_BASE}/concentration`);

export const fetchAdminClv = () => get<ClvReport>(`${ADMIN_BASE}/clv`);

export const fetchAdminManualQueue = () =>
  get<TrackedBetDto[]>(`${ADMIN_BASE}/manual-queue`);

export interface SettlementBackfillResult {
  settled: number;
  /** Still flagged for manual review after the retry. */
  stillFlagged?: number;
  /** Pending more than a day past start, after the retry. */
  stuckOver24h?: number;
}

/** Settlement SLO snapshot for the admin queue header. */
export interface SettlementHealth {
  stuckOver24h: number;
  stuckByLeague: Record<string, number>;
  flaggedForReview: number;
  autoSettledLast7d: number;
  medianHoursStartToSettle: number | null;
  percentSettledWithin8h: number | null;
}

export const fetchAdminSettlementHealth = () =>
  get<SettlementHealth>(`${ADMIN_BASE}/settlement-health`);

export const adminBackfillSettlement = async (): Promise<SettlementBackfillResult | null> => {
  try {
    const response = await apiClient.post(`${ADMIN_BASE}/backfill-settlement`);
    return response.data as SettlementBackfillResult;
  } catch (error) {
    console.error("[performance] settlement backfill failed", error);
    return null;
  }
};

export const adminBackfillWidth = async (
  days = 2
): Promise<{ attempted: number; backfilled: number; missed: number; days: number } | null> => {
  try {
    const response = await apiClient.post(`${ADMIN_BASE}/backfill-width?days=${days}`);
    return response.data as { attempted: number; backfilled: number; missed: number; days: number };
  } catch (error) {
    console.error("[performance] width backfill failed", error);
    return null;
  }
};

export const fetchAdminAgentConfig = () => get<EvAgentConfig>(`${ADMIN_BASE}/config`);

export const updateAdminAgentConfig = async (
  patch: EvAgentConfigUpdate
): Promise<EvAgentConfig | null> => {
  try {
    const response = await apiClient.put(`${ADMIN_BASE}/config`, patch);
    return response.data as EvAgentConfig;
  } catch (error) {
    console.error("[performance] config update failed", error);
    return null;
  }
};

// ── Sharp agent admin config ─────────────────────────────────────────

export interface SharpAgentConfig {
  track: "SHARP";
  enabled: boolean;
  startingBankroll: number;
  kellyMultiplier: number;
  minOddsAmerican: number;
  maxOddsAmerican: number;
  minMinutesToStart: number;
  maxBetsPerEvent: number;
  maxBetsPerDay: number;
  maxBetsPerRun: number;
  selectionIntervalMs: number;
  selectionIntervalMinutes: number;
  minStake: number;
  maxStake: number;
  minConfidenceScore: number;
  minBackingLiquidity: number;
  minEdgePoints: number;
  maxBookAgeMinutes: number;
  betsPlacedToday: number;
  hasPersistedOverrides: boolean;
}

export type SharpAgentConfigUpdate = Partial<
  Omit<SharpAgentConfig, "track" | "selectionIntervalMinutes" | "betsPlacedToday" | "hasPersistedOverrides">
>;

export const fetchAdminSharpConfig = () =>
  get<SharpAgentConfig>(`${ADMIN_BASE}/sharp-config`);

export const updateAdminSharpConfig = async (
  patch: SharpAgentConfigUpdate
): Promise<SharpAgentConfig | null> => {
  try {
    const response = await apiClient.put(`${ADMIN_BASE}/sharp-config`, patch);
    return response.data as SharpAgentConfig;
  } catch (error) {
    console.error("[performance] sharp config update failed", error);
    return null;
  }
};

// ── Traders agent admin (positions, jobs, config) ────────────────────

/** Admin position rows carry mark/resolution diagnostics on top of the public DTO. */
export interface AdminTraderPositionDto extends TraderPositionDto {
  conditionId?: string | null;
  tokenId?: string | null;
  agentRunId?: number | null;
  entrySlippagePct?: number | null;
  lastUnresolvedReason?: string | null;
  resolutionSource?: string | null;
  resolutionDiagnostic?: string | null;
}

export type TradersAdminJob = "run-selection" | "run-mark" | "run-resolve";

export interface TradersAgentConfig {
  track: "TRADERS";
  enabled: boolean;
  stakePerPosition: number;
  useBoardTargetSize: boolean;
  minScore: number;
  maxOpenPositions: number;
  maxNewPositionsPerDay: number;
  maxSlippagePct: number;
  minEntryPrice: number;
  maxEntryPrice: number;
  selectionIntervalMs: number;
  voidAfterDays: number;
  positionsOpenedToday: number;
  openPositions: number;
  hasPersistedOverrides: boolean;
}

export type TradersAgentConfigUpdate = Partial<
  Omit<TradersAgentConfig, "track" | "positionsOpenedToday" | "openPositions" | "hasPersistedOverrides">
>;

const TRADERS_ADMIN_BASE = `${ADMIN_BASE}/traders`;

export const fetchAdminTradersPositions = (
  page = 0,
  size = 50,
  status?: string
) =>
  get<{
    positions: AdminTraderPositionDto[];
    totalPages: number;
    totalElements: number;
  }>(
    `${TRADERS_ADMIN_BASE}/positions?page=${page}&size=${size}${
      status ? `&status=${encodeURIComponent(status)}` : ""
    }`
  );

export const adminResolveTraderPosition = async (
  positionId: number,
  status: string,
  terminalPrice?: number
): Promise<AdminTraderPositionDto | null> => {
  try {
    const response = await apiClient.post(
      `${TRADERS_ADMIN_BASE}/positions/${positionId}/resolve`,
      { status, terminalPrice }
    );
    return response.data as AdminTraderPositionDto;
  } catch (error) {
    console.error("[performance] trader position resolve failed", error);
    return null;
  }
};

export const adminRunTradersJob = async (
  job: TradersAdminJob
): Promise<Record<string, unknown> | null> => {
  try {
    const response = await apiClient.post(`${TRADERS_ADMIN_BASE}/${job}`);
    return response.data as Record<string, unknown>;
  } catch (error) {
    console.error(`[performance] traders ${job} failed`, error);
    return null;
  }
};

export const fetchAdminTradersConfig = () =>
  get<TradersAgentConfig>(`${TRADERS_ADMIN_BASE}/config`);

export const updateAdminTradersConfig = async (
  patch: TradersAgentConfigUpdate
): Promise<TradersAgentConfig | null> => {
  try {
    const response = await apiClient.put(`${TRADERS_ADMIN_BASE}/config`, patch);
    return response.data as TradersAgentConfig;
  } catch (error) {
    console.error("[performance] traders config update failed", error);
    return null;
  }
};

export const adminManualSettle = async (
  betId: number,
  status: string,
  actualResultValue?: number
): Promise<TrackedBetDto | null> => {
  try {
    const response = await apiClient.post(`${ADMIN_BASE}/bets/${betId}/settle`, {
      status,
      actualResultValue,
    });
    return response.data as TrackedBetDto;
  } catch (error) {
    console.error("[performance] manual settle failed", error);
    return null;
  }
};

export const adminResolveAssist = async (
  betId: number
): Promise<SettlementAssistResponse | null> => {
  try {
    const response = await apiClient.post(`${ADMIN_BASE}/bets/${betId}/resolve-assist`);
    return response.data as SettlementAssistResponse;
  } catch (error) {
    console.error("[performance] resolve assist failed", error);
    return null;
  }
};

// ── Formatting helpers shared by performance UI ──────────────────────

export const fmtMoney = (v: number | null | undefined, signed = false): string => {
  if (v == null) return "—";
  const sign = signed && v > 0 ? "+" : "";
  return `${sign}$${Math.abs(v) >= 1000 ? v.toLocaleString(undefined, { maximumFractionDigits: 0 }) : v.toFixed(2)}`.replace("$-", "-$");
};

export const fmtPct = (v: number | null | undefined, signed = false): string => {
  if (v == null) return "—";
  const sign = signed && v > 0 ? "+" : "";
  return `${sign}${v.toFixed(1)}%`;
};

export const fmtOdds = (v: number | null | undefined): string => {
  if (v == null) return "—";
  return v > 0 ? `+${v}` : `${v}`;
};

/** Prediction-market price in cents — 0.42 → "42¢". One decimal only if needed. */
export const fmtCents = (v: number | null | undefined): string => {
  if (v == null) return "—";
  return `${Number((v * 100).toFixed(1))}¢`;
};

/** EV green when positive, red when negative — green must prove something. */
export const profitTone = (v: number | null | undefined): string => {
  if (v == null) return "text-white/60";
  if (v > 0) return "text-[#00ff88] drop-shadow-[0_0_5px_rgba(0,255,136,0.8)]";
  if (v < 0) return "text-[#ff0044] drop-shadow-[0_0_5px_rgba(255,0,68,0.8)]";
  return "text-white/75";
};

/** Daily P/L for calendar cells — settled profit on the day the event ended. */
export const dayGain = (day: CalendarDay): number | null => {
  if (day.settledBets > 0) return day.totalActualProfit;
  return null;
};

/**
 * Track-aware daily P/L. Traders day rows carry the full day equity delta
 * (realized plus unrealized moves) in totalActualProfit even with nothing
 * resolved that day, so the settled-bets gate doesn't apply.
 */
export const dayGainForTrack = (
  day: CalendarDay,
  track?: PerformanceTrack
): number | null => {
  if (track === "traders") return day.totalActualProfit ?? null;
  return dayGain(day);
};

export const dayGainPercent = (day: CalendarDay): number | null => {
  if (day.actualRoiPercent != null) return day.actualRoiPercent;
  const gain = dayGain(day);
  if (gain != null && day.totalStaked > 0) return (gain / day.totalStaked) * 100;
  return null;
};

/** Compact dollar format for calendar tiles (Pikkit-style). */
export const fmtCalendarAmount = (v: number): string => {
  const abs = Math.abs(v);
  const sign = v < 0 ? "-" : "";
  if (abs >= 1000) {
    return `${sign}$${abs.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  }
  if (abs >= 100 || Number.isInteger(abs)) {
    return `${sign}$${Math.round(abs)}`;
  }
  return `${sign}$${abs.toFixed(1).replace(/\.0$/, "")}`;
};
